import { FormProvider, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useNavigate, useParams } from "react-router-dom"
import { useDispatch } from "react-redux"
import CardContent from "@mui/material/CardContent"
import Box from "@mui/material/Box"
import Stack from "@mui/material/Stack"
import Typography from "@mui/material/Typography"
import fp from "lodash/fp"

import { AccountCard, LogoContainer } from "widgets/styled/containers"
import { IMAGOTYPE } from "../../helpers/utils/constants"
import LoadingButton from "../../widgets/common/LoadingButton"
import NewPasswordInput from "../../widgets/common/LazyNewPasswordInput"
import { useResetPasswordMutation } from "../../features/api"
import ResultCard from "../../widgets/common/ResultCard"
import ControlledPasswordInput from "../../widgets/common/ControlledPasswordInput"
import { updateCode } from "../../features/store/authSlice"

export interface ResetPasswordForm {
  newPassword: string
  newPasswordConfirmation: string
}

const PasswordRecover = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { code, username } = useParams()
  const methods = useForm<ResetPasswordForm>({
    mode: "all",
    defaultValues: {
      newPassword: "",
      newPasswordConfirmation: "",
    },
  })
  const [resetPassword, { isLoading, isSuccess, isError, error }] =
    useResetPasswordMutation()

  const onSubmit = async (data: ResetPasswordForm) => {
    const { newPassword } = data
    resetPassword({
      secret: code as string,
      password: newPassword,
      username: username as string,
    })
      .unwrap()
      .then((response) => {
        dispatch(
          updateCode({
            code: response?.code,
          }),
        )
      })
  }

  if (isSuccess) {
    return (
      <ResultCard
        type="success"
        text={t("recover.RESET_SUCCESSFUL")}
        button={t("generic.RETURN_TO") + t("login.LOGIN_PAGE")}
        onClick={() => navigate("/accounts/login")}
      />
    )
  } else if (isError) {
    const message =
      (fp.get("data.message", error) || t("recover.RESET_LINK_FAILED")) +
      ". " +
      t("error.TRY_AGAIN_LATER")
    return (
      <ResultCard
        type="error"
        text={message}
        button={t("generic.RETURN_TO") + t("login.LOGIN_PAGE")}
        onClick={() => navigate("/accounts/login")}
      />
    )
  }

  return (
    <>
      <AccountCard
        sx={{ maxWidth: 360, minWidth: 280, width: "50%", textAlign: "center" }}
        elevation={4}
      >
        <LogoContainer disableGutters>
          <Box component="img" src={IMAGOTYPE} sx={{ width: "60%" }} />
        </LogoContainer>
        <CardContent sx={{ px: "7%" }}>
          <Typography variant="body2" component="p" align="center" sx={{ mb: 2, mt: 1 }}>
            {t("recover.ENTER_NEW_PASSWORD")}
          </Typography>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <FormProvider {...methods}>
              <Stack spacing={2}>
                <NewPasswordInput methods={methods} />
                <ControlledPasswordInput
                  size="small"
                  placeholder=""
                  label={t("register.RETYPE_PASSWORD")}
                  variant="outlined"
                  {...methods.register("newPasswordConfirmation", {
                    required: t("generic.FIELD_REQUIRED"),
                    validate: {
                      matchesPreviousPassword: (value) => {
                        const { newPassword } = methods.getValues()
                        return newPassword === value || t("register.PASSWORD_MATCH")
                      },
                    },
                  })}
                />
                <LoadingButton
                  loading={isLoading}
                  variant="contained"
                  styles={{ mt: 1, mb: 1 }}
                >
                  {t("recover.RESET")}
                </LoadingButton>
              </Stack>
            </FormProvider>
          </form>
        </CardContent>
      </AccountCard>
    </>
  )
}

export default PasswordRecover
